import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Pencil, Plus, Power, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const ROLES = ["fleet_manager", "auditor", "viewer"];

/** Fleet user administration: edit email/name/role, reset password, activate/deactivate, create.
 *  Rendered inside Settings for anyone with admin:users. */
export default function UserManager() {
  const { can, user } = useAuth();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/auth/users")).data, enabled: can("admin:users") });
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState<any>({});
  const [pwFor, setPwFor] = useState<string | null>(null);
  const [pw, setPw] = useState("");
  const [creating, setCreating] = useState(false);
  const [nu, setNu] = useState<any>({ name: "", email: "", role: "viewer", password: "" });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const save = useMutation({
    mutationFn: (id: string) => api.patch(`/auth/users/${id}`, edit),
    onSuccess: () => { invalidate(); setEditing(null); toast.success("User updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const resetPw = useMutation({
    mutationFn: (id: string) => api.post(`/auth/users/${id}/reset-password`, { password: pw }),
    onSuccess: (r: any) => { setPwFor(null); setPw(""); toast.success(`Password reset for ${r.data.email}`); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: any) => api.patch(`/auth/users/${id}`, { active }),
    onSuccess: () => { invalidate(); toast.success("Updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/users/${id}`),
    onSuccess: () => { invalidate(); toast.success("User removed"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const create = useMutation({
    mutationFn: () => api.post("/auth/users", nu),
    onSuccess: () => { invalidate(); setCreating(false); setNu({ name: "", email: "", role: "viewer", password: "" }); toast.success("User created"); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!can("admin:users")) return null;

  return (
    <div className="tg-card p-5" data-testid="settings-users">
      <div className="flex items-center justify-between mb-3">
        <div className="tg-label">Users &amp; roles</div>
        <Button size="sm" variant="outline" data-testid="user-new" onClick={() => setCreating((v) => !v)}
                className="h-7 text-[11px] border-[#26334d] bg-transparent text-slate-200">
          <Plus className="h-3 w-3 mr-1" />New user
        </Button>
      </div>

      {creating && (
        <div className="border border-[#26334d] rounded-md p-3 mb-3 grid gap-2" data-testid="user-new-form">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name" data-testid="user-new-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} className="h-8 bg-[#0f141f] border-[#26334d] text-xs" />
            <Input placeholder="email@fleet.com" data-testid="user-new-email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="h-8 bg-[#0f141f] border-[#26334d] text-xs" />
            <select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })} data-testid="user-new-role"
                    className="h-8 rounded-md bg-[#0f141f] border border-[#26334d] text-xs px-2 text-slate-100">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Input placeholder="Password (min 6)" type="password" data-testid="user-new-password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} className="h-8 bg-[#0f141f] border-[#26334d] text-xs" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" data-testid="user-create" disabled={!nu.name || !nu.email || nu.password.length < 6 || create.isPending} onClick={() => create.mutate()} className="h-7 text-[11px] bg-blue-600 hover:bg-blue-500 text-white">Create</Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)} className="h-7 text-[11px] text-slate-400">Cancel</Button>
          </div>
        </div>
      )}

      <div className="divide-y divide-[#26334d]">
        {(users.data || []).map((u: any) => {
          const isMe = u.id === (user as any)?.id;
          return (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="py-2">
              {editing === u.id ? (
                <div className="grid gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid={`user-email-${u.id}`} value={edit.email ?? u.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="h-8 bg-[#0f141f] border-[#26334d] text-xs" />
                    <Input value={edit.name ?? u.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="h-8 bg-[#0f141f] border-[#26334d] text-xs" />
                    <select value={edit.role ?? u.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}
                            className="h-8 rounded-md bg-[#0f141f] border border-[#26334d] text-xs px-2 text-slate-100">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" data-testid={`user-save-${u.id}`} disabled={save.isPending} onClick={() => save.mutate(u.id)} className="h-7 text-[11px] bg-blue-600 hover:bg-blue-500 text-white"><Check className="h-3 w-3 mr-1" />Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-7 text-[11px] text-slate-400"><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-200 flex-1 truncate">
                    {u.name} <span className="text-slate-500 text-xs">{u.email}</span>
                    {!u.active && <span className="ml-1 font-mono text-[9px] text-rose-400 border border-rose-500/30 rounded px-1">INACTIVE</span>}
                    {isMe && <span className="ml-1 font-mono text-[9px] text-slate-500">you</span>}
                  </span>
                  <span className="font-mono text-xs text-blue-300">{u.role}{u.effectiveRole !== u.role ? ` → ${u.effectiveRole}` : ""}</span>
                  <div className="flex items-center gap-0.5">
                    <Button size="icon" variant="ghost" title="Edit" data-testid={`user-edit-${u.id}`} onClick={() => { setEditing(u.id); setEdit({}); }} className="h-7 w-7 text-slate-500 hover:text-slate-200"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" title="Reset password" data-testid={`user-reset-${u.id}`} onClick={() => { setPwFor(u.id); setPw(""); }} className="h-7 w-7 text-slate-500 hover:text-amber-300"><KeyRound className="h-3.5 w-3.5" /></Button>
                    {!isMe && <Button size="icon" variant="ghost" title={u.active ? "Deactivate" : "Activate"} onClick={() => toggle.mutate({ id: u.id, active: !u.active })} className={cn("h-7 w-7", u.active ? "text-slate-500 hover:text-amber-300" : "text-emerald-400")}><Power className="h-3.5 w-3.5" /></Button>}
                    {!isMe && <Button size="icon" variant="ghost" title="Delete" data-testid={`user-delete-${u.id}`} onClick={() => del.mutate(u.id)} className="h-7 w-7 text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </div>
              )}
              {pwFor === u.id && (
                <div className="flex gap-2 mt-2" data-testid={`user-pw-form-${u.id}`}>
                  <Input type="password" placeholder="New password (min 6)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid={`user-pw-${u.id}`} className="h-8 bg-[#0f141f] border-[#26334d] text-xs" />
                  <Button size="sm" disabled={pw.length < 6 || resetPw.isPending} onClick={() => resetPw.mutate(u.id)} data-testid={`user-pw-save-${u.id}`} className="h-8 text-[11px] bg-amber-600 hover:bg-amber-500 text-white">Set password</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPwFor(null)} className="h-8 text-[11px] text-slate-400">Cancel</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
